import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  buildContent,
  forecast,
  resolveCombat,
  Rng,
  statsAtLevel,
  type ClassDef,
  type Combatant,
  type CombatResult,
  type Stats,
  type TerrainDef,
  type WeaponDef,
} from '@oz/rules';
import { fetchAdminContent } from '../../admin/contentApi.js';

/** A ficha de um personagem do acervo, do jeito que o painel a grava. */
interface CharacterDef {
  readonly id: number;
  readonly name: string;
  readonly classId: number;
  readonly level?: number;
  readonly equipment?: readonly number[];
  readonly pack?: string;
}

/** A ficha de um monstro do acervo. */
interface EnemyDef {
  readonly id: number;
  readonly name: string;
  readonly bases: Stats;
  readonly pack?: string;
}

/**
 * Quem pode ser carregado num dos lados do duelo.
 *
 * O simulador começou com quatro perfis escritos à mão — um "Dragão Ancião"
 * com 55 de PV que não existe em lugar nenhum do jogo. Números inventados
 * respondem a uma pergunta que ninguém fez: o que interessa a quem balanceia é
 * como o esquadrão REAL se sai contra o bestiário REAL, com a arma que a
 * unidade de fato carrega. Daqui saem os dois lados.
 */
interface Fighter {
  readonly key: string;
  readonly name: string;
  readonly kind: 'heroi' | 'monstro';
  readonly archetype: string;
  readonly con: number;
  readonly stats: Stats;
  readonly weaponId: number | null;
  readonly pack?: string | undefined;
}

/** Quantos duelos a amostra roda para estimar quem vence. */
const TRIAL_OPTIONS = [50, 200, 1000] as const;

const DEFAULT_TERRAINS: Record<string, TerrainDef> = {
  planicie: { cost: 1, avoid: 0, defense: 0, heal: 0 },
  estrada: { cost: 1, avoid: 0, defense: 0, heal: 0 },
  floresta: { cost: 2, avoid: 20, defense: 1, heal: 0 },
  colina: { cost: 2, avoid: 20, defense: 1, heal: 0 },
  montanha: { cost: 4, avoid: 30, defense: 2, heal: 0 },
  ruina: { cost: 2, avoid: 20, defense: 1, heal: 0 },
  forte: { cost: 2, avoid: 20, defense: 2, heal: 10 },
};

const DEFAULT_TRIANGLE = {
  beats: {
    espada: 'machado',
    machado: 'lanca',
    lanca: 'espada',
  },
  damageBonus: 1,
  hitBonus: 15,
};

export function CombatSimulator(): JSX.Element {
  const [classes, setClasses] = useState<ClassDef[]>([]);
  const [weapons, setWeapons] = useState<WeaponDef[]>([]);
  const [loading, setLoading] = useState(true);

  // Atacante (Lado A)
  const [attackerName, setAttackerName] = useState('Distortidus');
  const [attackerArchetype, setAttackerArchetype] = useState('mirmidao');
  const [attackerHp, setAttackerHp] = useState(25);
  const [attackerMaxHp, setAttackerMaxHp] = useState(25);
  const [attackerCon, setAttackerCon] = useState(7);
  const [attackerWeaponId, setAttackerWeaponId] = useState<number>(1);
  const [attackerTerrainKey, setAttackerTerrainKey] = useState('planicie');
  const [attackerStats, setAttackerStats] = useState<Stats>({
    hp: 25,
    str: 8,
    mag: 2,
    skl: 9,
    spd: 10,
    lck: 6,
    def: 6,
    res: 4,
  });

  // Defensor (Lado B)
  const [defenderName, setDefenderName] = useState('Goblin da Guarda');
  const [defenderArchetype, setDefenderArchetype] = useState('infantaria');
  const [defenderHp, setDefenderHp] = useState(20);
  const [defenderMaxHp, setDefenderMaxHp] = useState(20);
  const [defenderCon, setDefenderCon] = useState(6);
  const [defenderWeaponId, setDefenderWeaponId] = useState<number>(2);
  const [defenderTerrainKey, setDefenderTerrainKey] = useState('planicie');
  const [defenderStats, setDefenderStats] = useState<Stats>({
    hp: 20,
    str: 6,
    mag: 1,
    skl: 5,
    spd: 5,
    lck: 3,
    def: 5,
    res: 2,
  });

  // Distância do combate (1 = adjacente, 2 = à distância)
  const [distance, setDistance] = useState<number>(1);

  /*
   * Quantas trocas de golpes o duelo simula.
   *
   * Um combate do Fire Emblem é UMA troca — o atacante bate, o defensor
   * revida, e quem for 4 pontos mais rápido bate de novo. Mas a pergunta de
   * quem balanceia raramente cabe numa troca só: "esta unidade AGUENTA o
   * chefe?" só se responde encostando os dois por vários turnos. Cada rodada
   * aqui é um turno de combate, com o PV atravessando de uma para a outra.
   */
  const [rounds, setRounds] = useState(1);
  /** Quem começa batendo em cada rodada. */
  const [initiative, setInitiative] = useState<'atacante' | 'alterna'>('atacante');
  const [trials, setTrials] = useState<number>(200);

  // Quem está carregado em cada lado, para o seletor mostrar a escolha.
  const [attackerPick, setAttackerPick] = useState('');
  const [defenderPick, setDefenderPick] = useState('');

  const [characters, setCharacters] = useState<CharacterDef[]>([]);
  const [enemies, setEnemies] = useState<EnemyDef[]>([]);

  // Resultado de simulação real com dados: uma entrada por rodada.
  const [simResult, setSimResult] = useState<readonly CombatResult[] | null>(null);
  const [odds, setOdds] = useState<{
    attacker: number; defender: number; draw: number; rounds: number; trials: number;
  } | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [clsData, wpnData, chrData, enmData] = await Promise.all([
          fetchAdminContent<ClassDef>('classes'),
          fetchAdminContent<WeaponDef>('weapons'),
          fetchAdminContent<CharacterDef>('characters').catch(() => []),
          fetchAdminContent<EnemyDef>('enemies').catch(() => []),
        ]);
        const clsList = clsData.map((c) => ({ ...c.data, id: c.contentId, name: c.name }));
        const wpnList = wpnData.map((w) => ({ ...w.data, id: w.contentId, name: w.name }));
        setClasses(clsList);
        setWeapons(wpnList);
        setCharacters(chrData.map((c) => ({ ...c.data, id: c.contentId, name: c.name })));
        setEnemies(enmData.map((e) => ({ ...e.data, id: e.contentId, name: e.name })));
        if (wpnList.length > 0 && wpnList[0]) {
          setAttackerWeaponId(wpnList[0].id);
          setDefenderWeaponId(wpnList[1]?.id ?? wpnList[0].id);
        }
      } catch (err) {
        console.error('Erro ao carregar dados para o simulador:', err);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  const content = useMemo(() => {
    return buildContent({
      classes,
      weapons,
      terrain: DEFAULT_TERRAINS,
      triangle: DEFAULT_TRIANGLE,
    });
  }, [classes, weapons]);

  const attackerWeapon = weapons.find((w) => w.id === attackerWeaponId) ?? null;
  const defenderWeapon = weapons.find((w) => w.id === defenderWeaponId) ?? null;

  const attackerCombatant: Combatant = useMemo(
    () => ({
      stats: attackerStats,
      con: attackerCon,
      weapon: attackerWeapon,
      terrain: DEFAULT_TERRAINS[attackerTerrainKey],
      archetype: attackerArchetype,
      currentHp: attackerHp,
    }),
    [attackerStats, attackerCon, attackerWeapon, attackerTerrainKey, attackerArchetype, attackerHp],
  );

  const defenderCombatant: Combatant = useMemo(
    () => ({
      stats: defenderStats,
      con: defenderCon,
      weapon: defenderWeapon,
      terrain: DEFAULT_TERRAINS[defenderTerrainKey],
      archetype: defenderArchetype,
      currentHp: defenderHp,
    }),
    [defenderStats, defenderCon, defenderWeapon, defenderTerrainKey, defenderArchetype, defenderHp],
  );

  const forecastData = useMemo(() => {
    if (classes.length === 0 || weapons.length === 0) return null;
    return forecast(content, attackerCombatant, defenderCombatant, distance);
  }, [content, attackerCombatant, defenderCombatant, distance, classes.length, weapons.length]);

  /**
   * O elenco de verdade, os dois grupos numa lista só.
   *
   * O herói tem classe e nível, e daí saem os atributos pelo caminho sem
   * sorteio (`statsAtLevel`) — o mesmo que o jogo usa para pré-visualizar uma
   * unidade. O monstro já traz os atributos prontos na ficha dele.
   */
  const fighters = useMemo<readonly Fighter[]>(() => {
    const out: Fighter[] = [];

    for (const c of characters) {
      const klass = classes.find((k) => k.id === c.classId);
      if (!klass) continue;
      const level = Math.max(1, c.level ?? 1);
      // `equipment` é a bolsa inicial (só armas); a primeira que existir é a titular.
      const weaponId = (c.equipment ?? []).find((id) => weapons.some((w) => w.id === id)) ?? null;
      out.push({
        key: `heroi:${c.id}`,
        name: `${c.name} · ${klass.name} Nv.${level}`,
        kind: 'heroi',
        archetype: klass.archetype ?? 'infantaria',
        con: klass.con ?? 7,
        stats: statsAtLevel(klass, level),
        weaponId,
        pack: c.pack,
      });
    }

    for (const e of enemies) {
      if (!e.bases) continue;
      out.push({
        key: `monstro:${e.id}`,
        name: e.name,
        kind: 'monstro',
        archetype: 'infantaria',
        con: 7,
        stats: e.bases,
        weaponId: null,
        pack: e.pack,
      });
    }

    return out;
  }, [characters, enemies, classes, weapons]);

  /** Põe um combatente do acervo num dos lados, campo a campo. */
  const loadFighter = (side: 'attacker' | 'defender', key: string): void => {
    const who = fighters.find((f) => f.key === key);
    if (!who) return;
    const hp = who.stats.hp;
    // O monstro não carrega arma na ficha; sem uma, ele não consegue nem
    // atacar nem revidar, e o duelo sairia vazio. A arma em uso no lado fica.
    const weaponId = who.weaponId ?? (side === 'attacker' ? attackerWeaponId : defenderWeaponId);

    if (side === 'attacker') {
      setAttackerPick(key);
      setAttackerName(who.name);
      setAttackerArchetype(who.archetype);
      setAttackerCon(who.con);
      setAttackerStats(who.stats);
      setAttackerMaxHp(hp);
      setAttackerHp(hp);
      setAttackerWeaponId(weaponId);
    } else {
      setDefenderPick(key);
      setDefenderName(who.name);
      setDefenderArchetype(who.archetype);
      setDefenderCon(who.con);
      setDefenderStats(who.stats);
      setDefenderMaxHp(hp);
      setDefenderHp(hp);
      setDefenderWeaponId(weaponId);
    }
    setSimResult(null);
    setOdds(null);
  };

  /**
   * Um duelo inteiro: até `rounds` trocas, com o PV passando de uma à outra.
   *
   * Devolve a lista de rodadas para quem quer ler o registro golpe a golpe, e
   * para quem só quer o placar basta olhar a última.
   */
  const duel = useCallback(
    (rng: Rng, limit: number): CombatResult[] => {
      const log: CombatResult[] = [];
      let aHp = attackerCombatant.currentHp;
      let dHp = defenderCombatant.currentHp;

      for (let round = 0; round < limit && aHp > 0 && dHp > 0; round++) {
        // Quem toma a iniciativa pode alternar: no tabuleiro, o turno seguinte
        // é do outro lado, e é ele quem escolhe encostar.
        const swap = initiative === 'alterna' && round % 2 === 1;
        const first = { ...(swap ? defenderCombatant : attackerCombatant), currentHp: swap ? dHp : aHp };
        const second = { ...(swap ? attackerCombatant : defenderCombatant), currentHp: swap ? aHp : dHp };

        const res = resolveCombat(content, first, second, distance, rng);
        if (swap) {
          dHp = res.attackerHpAfter;
          aHp = res.defenderHpAfter;
          // Normalizado para o ponto de vista do painel: "atacante" na tela é
          // sempre o lado esquerdo, tenha ele começado a rodada ou não.
          log.push({
            ...res,
            attackerHpAfter: aHp,
            defenderHpAfter: dHp,
            blows: res.blows.map((b) => ({
              ...b,
              side: b.side === 'atacante' ? ('defensor' as const) : ('atacante' as const),
            })),
          });
        } else {
          aHp = res.attackerHpAfter;
          dHp = res.defenderHpAfter;
          log.push(res);
        }
      }

      return log;
    },
    [content, attackerCombatant, defenderCombatant, distance, initiative],
  );

  const handleSimulateRound = () => {
    setOdds(null);
    setSimResult(duel(new Rng(Date.now()), Math.max(1, rounds)));
  };

  /**
   * A mesma batalha muitas vezes, para separar sorte de desequilíbrio.
   *
   * Uma rolagem só não diz nada: 73% de acerto erra com frequência, e quem
   * balanceia pelo resultado de um dado ajusta o jogo pelo azar da tarde.
   */
  const handleSimulateOdds = () => {
    const limit = Math.max(1, rounds);
    const rng = new Rng(Date.now());
    let attackerWins = 0;
    let defenderWins = 0;
    let totalRounds = 0;

    for (let i = 0; i < trials; i++) {
      const log = duel(rng, limit);
      const last = log[log.length - 1];
      totalRounds += log.length;
      if (!last) continue;
      if (last.defenderHpAfter <= 0) attackerWins++;
      else if (last.attackerHpAfter <= 0) defenderWins++;
    }

    setSimResult(null);
    setOdds({
      attacker: (attackerWins / trials) * 100,
      defender: (defenderWins / trials) * 100,
      draw: ((trials - attackerWins - defenderWins) / trials) * 100,
      rounds: totalRounds / trials,
      trials,
    });
  };

  if (loading) {
    return <div className="admin-loading-indicator">Carregando motor de combate e acervo…</div>;
  }

  return (
    <div className="combat-sim-page">
      <div className="combat-sim-header">
        <div className="sim-title-box">
          <h2>🎯 Simulador de Combate em Tempo Real</h2>
          <p className="muted">
            Calcula com exatidão as fórmulas do motor @oz/rules: dano real, acerto 2RN, chance de
            crítico, ataque duplo (VelAtq &gt;= +4) e triângulo de armas.
          </p>
        </div>

        <div className="sim-top-controls">
          <div className="distance-selector">
            <span className="distance-label">Distância do Ataque:</span>
            <button
              type="button"
              className={`dist-btn ${distance === 1 ? 'active' : ''}`}
              onClick={() => setDistance(1)}
            >
              1 Casa (Corpo a Corpo)
            </button>
            <button
              type="button"
              className={`dist-btn ${distance === 2 ? 'active' : ''}`}
              onClick={() => setDistance(2)}
            >
              2 Casas (Distância / Arco)
            </button>
          </div>

          <div className="distance-selector">
            <span className="distance-label">Rodadas de combate:</span>
            <input
              type="number"
              min="1"
              max="20"
              className="sim-rounds-input"
              value={rounds}
              onChange={(e) => setRounds(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              title="Cada rodada é um turno de combate; o PV atravessa de uma para a outra."
            />
            <button
              type="button"
              className={`dist-btn ${initiative === 'atacante' ? 'active' : ''}`}
              onClick={() => setInitiative('atacante')}
              title="O mesmo lado começa batendo em todas as rodadas."
            >
              Iniciativa fixa
            </button>
            <button
              type="button"
              className={`dist-btn ${initiative === 'alterna' ? 'active' : ''}`}
              onClick={() => setInitiative('alterna')}
              title="Os lados revezam quem encosta primeiro, como no tabuleiro."
            >
              Iniciativa alternada
            </button>
          </div>

          <div className="distance-selector">
            <span className="distance-label">Tamanho da amostra:</span>
            {TRIAL_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                className={`dist-btn ${trials === n ? 'active' : ''}`}
                onClick={() => setTrials(n)}
              >
                {n} batalhas
              </button>
            ))}
          </div>

          <div className="sim-run-row">
            <button
              type="button"
              className="sim-execute-btn"
              onClick={handleSimulateRound}
            >
              🎲 Rolar Duelo
            </button>
            <button
              type="button"
              className="sim-execute-btn secondary"
              onClick={handleSimulateOdds}
            >
              📊 Rodar Amostra
            </button>
          </div>
        </div>
      </div>

      {/* Visão de Previsão de Combate (HUD do GBA) */}
      {forecastData && (
        <div className="combat-forecast-banner">
          <div className="forecast-unit attacker-side">
            <div className="forecast-unit-name">{attackerName}</div>
            <div className="forecast-main-stats">
              <div className="stat-pill hp">
                <span className="lbl">PV</span>
                <span className="val">{attackerHp}/{attackerMaxHp}</span>
              </div>
              <div className="stat-pill dmg">
                <span className="lbl">Dano</span>
                <span className="val">
                  {forecastData.attacker.canAttack ? forecastData.attacker.damage : '--'}
                  {forecastData.attacker.attacks > 1 && <span className="double-tag">×2</span>}
                </span>
              </div>
              <div className="stat-pill hit">
                <span className="lbl">Acerto</span>
                <span className="val">{forecastData.attacker.canAttack ? `${forecastData.attacker.hit}%` : '--'}</span>
              </div>
              <div className="stat-pill crit">
                <span className="lbl">Crítico</span>
                <span className="val">{forecastData.attacker.canAttack ? `${forecastData.attacker.crit}%` : '--'}</span>
              </div>
            </div>

            <div className="forecast-badges-row">
              {forecastData.attacker.triangle === 'vantagem' && (
                <span className="triangle-badge advantage">▲ Vantagem no Triângulo (+1 Mt, +15% Hit)</span>
              )}
              {forecastData.attacker.triangle === 'desvantagem' && (
                <span className="triangle-badge disadvantage">▼ Desvantagem no Triângulo (-1 Mt, -15% Hit)</span>
              )}
              {forecastData.attacker.effective && (
                <span className="effective-badge">💥 Arma Eficaz (Poder ×3)!</span>
              )}
              {forecastData.attacker.attacks > 1 && (
                <span className="double-badge">⚡ Ataque Duplo (+4 VelAtq)</span>
              )}
              {!forecastData.attacker.canAttack && (
                <span className="no-attack-badge">⛔ Fora de Alcance</span>
              )}
            </div>
          </div>

          <div className="forecast-vs-divider">
            <span className="vs-circle">VS</span>
            <span className="vs-dist">{distance === 1 ? '1 Casa' : '2 Casas'}</span>
          </div>

          <div className="forecast-unit defender-side">
            <div className="forecast-unit-name">{defenderName}</div>
            <div className="forecast-main-stats">
              <div className="stat-pill hp">
                <span className="lbl">PV</span>
                <span className="val">{defenderHp}/{defenderMaxHp}</span>
              </div>
              <div className="stat-pill dmg">
                <span className="lbl">Dano</span>
                <span className="val">
                  {forecastData.defender.canAttack ? forecastData.defender.damage : '--'}
                  {forecastData.defender.attacks > 1 && <span className="double-tag">×2</span>}
                </span>
              </div>
              <div className="stat-pill hit">
                <span className="lbl">Acerto</span>
                <span className="val">{forecastData.defender.canAttack ? `${forecastData.defender.hit}%` : '--'}</span>
              </div>
              <div className="stat-pill crit">
                <span className="lbl">Crítico</span>
                <span className="val">{forecastData.defender.canAttack ? `${forecastData.defender.crit}%` : '--'}</span>
              </div>
            </div>

            <div className="forecast-badges-row">
              {forecastData.defender.triangle === 'vantagem' && (
                <span className="triangle-badge advantage">▲ Vantagem no Triângulo (+1 Mt, +15% Hit)</span>
              )}
              {forecastData.defender.triangle === 'desvantagem' && (
                <span className="triangle-badge disadvantage">▼ Desvantagem no Triângulo (-1 Mt, -15% Hit)</span>
              )}
              {forecastData.defender.effective && (
                <span className="effective-badge">💥 Arma Eficaz (Poder ×3)!</span>
              )}
              {forecastData.defender.attacks > 1 && (
                <span className="double-badge">⚡ Ataque Duplo (+4 VelAtq)</span>
              )}
              {!forecastData.defender.canAttack && (
                <span className="no-attack-badge">⛔ Não pode contra-atacar</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Amostra: a mesma batalha muitas vezes */}
      {odds && (
        <div className="combat-sim-log-box">
          <div className="log-header">
            <h3>📊 Amostra de {odds.trials} batalhas ({rounds} rodada{rounds > 1 ? 's' : ''} cada)</h3>
            <button type="button" className="log-close-btn" onClick={() => setOdds(null)}>
              ✕ Fechar
            </button>
          </div>
          <div className="sim-odds-grid">
            <div className="sim-odds-cell win">
              <span className="sim-odds-label">{attackerName} vence</span>
              <span className="sim-odds-value">{odds.attacker.toFixed(1)}%</span>
            </div>
            <div className="sim-odds-cell loss">
              <span className="sim-odds-label">{defenderName} vence</span>
              <span className="sim-odds-value">{odds.defender.toFixed(1)}%</span>
            </div>
            <div className="sim-odds-cell draw">
              <span className="sim-odds-label">Ninguém cai</span>
              <span className="sim-odds-value">{odds.draw.toFixed(1)}%</span>
            </div>
            <div className="sim-odds-cell">
              <span className="sim-odds-label">Rodadas até decidir</span>
              <span className="sim-odds-value">{odds.rounds.toFixed(1)}</span>
            </div>
          </div>
          <p className="admin-helper-text" style={{ padding: '0 1rem 0.8rem' }}>
            Uma rolagem só não separa azar de desequilíbrio: 73% de acerto erra com frequência.
            A amostra repete o mesmo confronto e mostra o que acontece na média.
          </p>
        </div>
      )}

      {/* Relatório de Combate Rolado, rodada a rodada */}
      {simResult && simResult.length > 0 && (() => {
        const last = simResult[simResult.length - 1]!;
        return (
          <div className="combat-sim-log-box">
            <div className="log-header">
              <h3>
                📜 Registro do duelo — {simResult.length} rodada{simResult.length > 1 ? 's' : ''} (RNG)
              </h3>
              <button type="button" className="log-close-btn" onClick={() => setSimResult(null)}>
                ✕ Fechar
              </button>
            </div>
            {simResult.map((round, r) => (
              <div key={r} className="log-round">
                <div className="log-round-head">
                  <span className="log-round-tag">Rodada {r + 1}</span>
                  <span className="log-round-hp">
                    {attackerName} <strong>{round.attackerHpAfter}</strong> PV ·{' '}
                    {defenderName} <strong>{round.defenderHpAfter}</strong> PV
                  </span>
                </div>
                <div className="log-blows-list">
                  {round.blows.length === 0 && (
                    <div className="log-blow-entry">
                      <span className="blow-miss">Nenhum golpe: ninguém alcança o outro daqui.</span>
                    </div>
                  )}
                  {round.blows.map((b, idx) => (
                    <div key={idx} className={`log-blow-entry ${b.side} ${b.result}`}>
                      <span className="blow-idx">Golpe #{idx + 1}:</span>
                      <span className="blow-actor">
                        {b.side === 'atacante' ? attackerName : defenderName}
                      </span>
                      {b.result === 'erro' ? (
                        <span className="blow-miss">ERROU o golpe!</span>
                      ) : (
                        <span className="blow-hit">
                          {b.result === 'critico' && <strong className="crit-text">💥 CRÍTICO! </strong>}
                          acertou causando <strong>{b.damage}</strong> de dano. (PV restante do alvo:{' '}
                          <strong>{b.targetHpAfter}</strong>)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="log-footer-summary">
              <span>
                PV Final {attackerName}: <strong>{last.attackerHpAfter}</strong>
              </span>
              <span>·</span>
              <span>
                PV Final {defenderName}: <strong>{last.defenderHpAfter}</strong>
              </span>
              <span>·</span>
              <span className="log-winner">
                {last.defenderHpAfter <= 0
                  ? `🏆 ${attackerName} venceu o confronto!`
                  : last.attackerHpAfter <= 0
                  ? `💀 ${defenderName} venceu o confronto!`
                  : `🛡️ Os dois sobreviveram às ${simResult.length} rodadas.`}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Painéis de Configuração dos Dois Lados */}
      <div className="combat-sim-grid">
        {/* Painel do Atacante */}
        <div className="combat-sim-side-panel">
          <div className="side-panel-header attacker">
            <h3>⚔️ Atacante (Iniciativa)</h3>
            <div className="sim-roster-picker">
              <select
                className="admin-select"
                value={attackerPick}
                onChange={(e) => loadFighter('attacker', e.target.value)}
              >
                <option value="">Carregar do acervo…</option>
                <optgroup label="Personagens">
                  {fighters
                    .filter((f) => f.kind === 'heroi')
                    .map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.name}{f.pack ? ` · ${f.pack}` : ''}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Inimigos">
                  {fighters
                    .filter((f) => f.kind === 'monstro')
                    .map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.name}{f.pack ? ` · ${f.pack}` : ''}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className="side-panel-content">
            <div className="admin-form-row">
              <label>Nome:</label>
              <input
                type="text"
                className="admin-input-text"
                value={attackerName}
                onChange={(e) => setAttackerName(e.target.value)}
              />
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Arquétipo:</label>
                <input
                  type="text"
                  className="admin-input-text"
                  value={attackerArchetype}
                  onChange={(e) => setAttackerArchetype(e.target.value)}
                />
              </div>

              <div className="admin-form-row">
                <label>Constituição (Con):</label>
                <input
                  type="number"
                  className="admin-input-number"
                  value={attackerCon}
                  onChange={(e) => setAttackerCon(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="admin-form-row">
              <label>Arma Equipada:</label>
              <select
                className="admin-select"
                value={attackerWeaponId}
                onChange={(e) => setAttackerWeaponId(Number(e.target.value))}
              >
                {weapons.map((w) => (
                  <option key={w.id} value={w.id}>
                    #{w.id}: {w.name} (Mt {w.might}, Hit {w.hit}%, {w.type})
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>PV Atual / Máximo:</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="number"
                    className="admin-input-number"
                    value={attackerHp}
                    onChange={(e) => setAttackerHp(Number(e.target.value))}
                  />
                  <input
                    type="number"
                    className="admin-input-number"
                    value={attackerMaxHp}
                    onChange={(e) => setAttackerMaxHp(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="admin-form-row">
                <label>Terreno Atual:</label>
                <select
                  className="admin-select"
                  value={attackerTerrainKey}
                  onChange={(e) => setAttackerTerrainKey(e.target.value)}
                >
                  {Object.keys(DEFAULT_TERRAINS).map((t) => (
                    <option key={t} value={t}>
                      {t.toUpperCase()} (Esq +{DEFAULT_TERRAINS[t]?.avoid ?? 0}%, Def +{DEFAULT_TERRAINS[t]?.defense ?? 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h4 className="side-stats-title">Atributos Básicos:</h4>
            <div className="side-stats-mini-grid">
              {(['str', 'mag', 'skl', 'spd', 'lck', 'def', 'res'] as (keyof Stats)[]).map((st) => (
                <div key={st} className="mini-stat-field">
                  <label>{st.toUpperCase()}:</label>
                  <input
                    type="number"
                    value={attackerStats[st]}
                    onChange={(e) =>
                      setAttackerStats({ ...attackerStats, [st]: Number(e.target.value) })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Painel do Defensor */}
        <div className="combat-sim-side-panel">
          <div className="side-panel-header defender">
            <h3>🛡️ Defensor (Alvo)</h3>
            <div className="sim-roster-picker">
              <select
                className="admin-select"
                value={defenderPick}
                onChange={(e) => loadFighter('defender', e.target.value)}
              >
                <option value="">Carregar do acervo…</option>
                <optgroup label="Personagens">
                  {fighters
                    .filter((f) => f.kind === 'heroi')
                    .map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.name}{f.pack ? ` · ${f.pack}` : ''}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Inimigos">
                  {fighters
                    .filter((f) => f.kind === 'monstro')
                    .map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.name}{f.pack ? ` · ${f.pack}` : ''}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className="side-panel-content">
            <div className="admin-form-row">
              <label>Nome:</label>
              <input
                type="text"
                className="admin-input-text"
                value={defenderName}
                onChange={(e) => setDefenderName(e.target.value)}
              />
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Arquétipo:</label>
                <input
                  type="text"
                  className="admin-input-text"
                  value={defenderArchetype}
                  onChange={(e) => setDefenderArchetype(e.target.value)}
                />
              </div>

              <div className="admin-form-row">
                <label>Constituição (Con):</label>
                <input
                  type="number"
                  className="admin-input-number"
                  value={defenderCon}
                  onChange={(e) => setDefenderCon(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="admin-form-row">
              <label>Arma Equipada:</label>
              <select
                className="admin-select"
                value={defenderWeaponId}
                onChange={(e) => setDefenderWeaponId(Number(e.target.value))}
              >
                {weapons.map((w) => (
                  <option key={w.id} value={w.id}>
                    #{w.id}: {w.name} (Mt {w.might}, Hit {w.hit}%, {w.type})
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>PV Atual / Máximo:</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="number"
                    className="admin-input-number"
                    value={defenderHp}
                    onChange={(e) => setDefenderHp(Number(e.target.value))}
                  />
                  <input
                    type="number"
                    className="admin-input-number"
                    value={defenderMaxHp}
                    onChange={(e) => setDefenderMaxHp(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="admin-form-row">
                <label>Terreno Atual:</label>
                <select
                  className="admin-select"
                  value={defenderTerrainKey}
                  onChange={(e) => setDefenderTerrainKey(e.target.value)}
                >
                  {Object.keys(DEFAULT_TERRAINS).map((t) => (
                    <option key={t} value={t}>
                      {t.toUpperCase()} (Esq +{DEFAULT_TERRAINS[t]?.avoid ?? 0}%, Def +{DEFAULT_TERRAINS[t]?.defense ?? 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h4 className="side-stats-title">Atributos Básicos:</h4>
            <div className="side-stats-mini-grid">
              {(['str', 'mag', 'skl', 'spd', 'lck', 'def', 'res'] as (keyof Stats)[]).map((st) => (
                <div key={st} className="mini-stat-field">
                  <label>{st.toUpperCase()}:</label>
                  <input
                    type="number"
                    value={defenderStats[st]}
                    onChange={(e) =>
                      setDefenderStats({ ...defenderStats, [st]: Number(e.target.value) })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
